import { Mask } from "@/utils/mask";

export type PixKeyType = "cpf" | "cnpj" | "phone" | "email" | "random";

export const PIX_KEY_TYPES: {
  value: PixKeyType;
  label: string;
  placeholder: string;
}[] = [
  { value: "cpf", label: "CPF", placeholder: "000.000.000-00" },
  { value: "cnpj", label: "CNPJ", placeholder: "00.000.000/0000-00" },
  { value: "phone", label: "Telefone", placeholder: "(00) 00000-0000" },
  { value: "email", label: "E-mail", placeholder: "email@exemplo.com" },
  {
    value: "random",
    label: "Chave aleatória",
    placeholder: "00000000-0000-0000-0000-000000000000",
  },
];

const onlyDigits = (value: string) => value.replace(/\D/g, "");

export const isValidCpf = (value: string): boolean => {
  const cpf = onlyDigits(value);
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;

  for (let t = 9; t < 11; t++) {
    let sum = 0;
    for (let i = 0; i < t; i++) sum += Number(cpf[i]) * (t + 1 - i);
    if (Number(cpf[t]) !== ((10 * sum) % 11) % 10) return false;
  }
  return true;
};

export const isValidCnpj = (value: string): boolean => {
  const cnpj = onlyDigits(value);
  if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) return false;

  const calc = (base: string) => {
    const weights =
      base.length === 12
        ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
        : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const sum = base
      .split("")
      .reduce((acc, d, i) => acc + Number(d) * weights[i], 0);
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };

  const first = calc(cnpj.slice(0, 12));
  const second = calc(cnpj.slice(0, 12) + first);
  return Number(cnpj[12]) === first && Number(cnpj[13]) === second;
};

/** Máscara de exibição enquanto a pessoa digita. */
export const maskPixKey = (type: PixKeyType, value: string): string => {
  switch (type) {
    case "cpf":
      return Mask.maskCpf(value);
    case "cnpj":
      return Mask.maskString(onlyDigits(value).slice(0, 14), "##.###.###/####-##");
    case "phone":
      return Mask.maskString(onlyDigits(value).slice(0, 11), "(##) #####-####");
    case "email":
      return value.trim().toLowerCase();
    default:
      return value.trim().toLowerCase();
  }
};

/** Valor que vai para a API (o formato que o BTG espera). */
export const toPixKeyValue = (type: PixKeyType, value: string): string => {
  switch (type) {
    case "cpf":
    case "cnpj":
      return onlyDigits(value);
    case "phone":
      return `+55${onlyDigits(value)}`;
    default:
      return value.trim().toLowerCase();
  }
};

/** Retorna a mensagem de erro, ou null quando a chave é válida. */
export const validatePixKey = (
  type: PixKeyType,
  value: string
): string | null => {
  const digits = onlyDigits(value);

  switch (type) {
    case "cpf":
      return isValidCpf(value) ? null : "CPF inválido";
    case "cnpj":
      return isValidCnpj(value) ? null : "CNPJ inválido";
    case "phone":
      return digits.length === 10 || digits.length === 11
        ? null
        : "Telefone inválido. Informe DDD e número";
    case "email":
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
        ? null
        : "E-mail inválido";
    default:
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        value.trim()
      )
        ? null
        : "Chave aleatória inválida (formato 8-4-4-4-12)";
  }
};

/**
 * Lê uma chave já guardada (formato enviado à API) e devolve o tipo e o texto formatado
 * para preencher o formulário. Retorna null quando o tipo não dá para saber com segurança
 * (por exemplo, 11 dígitos que não são um CPF válido).
 */
export const parsePixKey = (
  key?: string | null
): { type: PixKeyType; display: string } | null => {
  const value = (key || "").trim();
  if (!value) return null;

  if (value.includes("@")) {
    return { type: "email", display: maskPixKey("email", value) };
  }

  if (value.startsWith("+")) {
    const digits = onlyDigits(value);
    const local = digits.startsWith("55") ? digits.slice(2) : digits;
    if (local.length === 10 || local.length === 11) {
      return { type: "phone", display: maskPixKey("phone", local) };
    }
    return null;
  }

  if (validatePixKey("random", value) === null) {
    return { type: "random", display: maskPixKey("random", value) };
  }

  const digits = onlyDigits(value);
  if (digits.length === 14 && isValidCnpj(digits)) {
    return { type: "cnpj", display: maskPixKey("cnpj", digits) };
  }
  if (digits.length === 11 && isValidCpf(digits)) {
    return { type: "cpf", display: maskPixKey("cpf", digits) };
  }

  return null;
};
